// src/components/OperadorInyeccion/Graficas/Graficas.jsx
import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  ReferenceLine,
} from "recharts";

/* ================== CONSTS & COLORS ================== */
const turnoColors = {
  "Turno 1": "#60a5fa",
  "Turno 2": "#34d399",
  "Turno 3": "#f87171",
};

const defaultColors = [
  "#60a5fa",
  "#34d399",
  "#f87171",
  "#fbbf24",
  "#a78bfa",
  "#f472b6",
  "#4ade80",
];

const DARK_TEXT = "#e5e7eb";
const DARK_AXIS = "#9ca3af";
const DARK_GRID = "rgba(255,255,255,0.06)";
const CAPACIDAD_DIA_100 = 35000; // piezas por día completo (3 turnos)

/* ================== HELPERS ================== */
// Para cada mes (year, month0) calcula días L–V, sábados y turnos teóricos
const getMonthWorkInfo = (year, month0) => {
  const start = new Date(year, month0, 1);
  const end = new Date(year, month0 + 1, 0);
  let diasLV = 0, diasSab = 0, turnosTeoricos = 0;
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow >= 1 && dow <= 5) { diasLV++; turnosTeoricos += 3; }
    else if (dow === 6) { diasSab++; turnosTeoricos += 2; }
  }
  return { diasLV, diasSab, diasTotales: diasLV + diasSab, turnosTeoricos };
};

// Si el padre pasó `fecha` como prop, podrías detectar mes; aquí usamos filtered
const makeTicksStyle = size => ({ fill: DARK_AXIS, fontSize: size });

// Tooltip oscuro
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "rgba(17,24,39,0.9)",
      color: DARK_TEXT,
      padding: "8px 12px",
      borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.1)",
      fontSize: 12
    }}>
      <div style={{ fontWeight:600, marginBottom:4 }}>{label}</div>
      {payload.map((p,i) => <div key={i}>{p.name}: {formatter ? formatter(p.value) : p.value}</div>)}
    </div>
  );
};

const RotatedXAxis = ({ dataKey }) => (
  <XAxis
    dataKey={dataKey}
    angle={-65}
    textAnchor="end"
    interval={0}
    height={90}
    tick={makeTicksStyle(11)}
    stroke={DARK_AXIS}
  />
);

const labelStyle = {
  fill: DARK_TEXT,
  fontSize: 12,
  textShadow: "0 0 3px rgba(0,0,0,0.6)",
};

const getBarSize = len => len <= 6 ? 90 : len <= 12 ? 60 : 35;

/* ================== COMPONENT ================== */
const Graficas = ({ data, busquedaOp }) => {
  const term = busquedaOp.trim().toLowerCase();
  const hasInyectora = data.some(r => r.inyectora.toLowerCase() === term);
  const hasOperador = data.some(r => r.operador.toLowerCase() === term);
  const hasTurno = data.some(r => r.turno.toLowerCase() === term);
  const noData = !hasInyectora && !hasOperador && !hasTurno;

  const ChartBlock = ({ title, dataKeyX, rows, colorMap, capLine }) => {
    const barSize = getBarSize(rows.length);
    return (
      <>
        {title && <h3 className="oi-chart-title text-center mb-4">{title}</h3>}
        <ResponsiveContainer width="100%" height={600}>
          <BarChart
            data={rows}
            margin={{ top:20, right:30, left:20, bottom:140 }}
            barCategoryGap={20}
            barGap={8}
          >
            <CartesianGrid stroke={DARK_GRID} strokeDasharray="3 3"/>
            <RotatedXAxis dataKey={dataKeyX}/>
            <YAxis
              tickFormatter={v=>v.toLocaleString("es-MX")}
              tick={makeTicksStyle(12)}
              stroke={DARK_AXIS}
            />
            <Tooltip
              formatter={v=>v.toLocaleString("es-MX")}
              content={CustomTooltip}
            />
            {capLine != null && (
              <ReferenceLine
                y={capLine}
                stroke="#fbbf24"
                strokeDasharray="4 4"
                label={{
                  value:`Capacidad: ${capLine.toLocaleString("es-MX")}`,
                  position:"top",
                  fill:DARK_TEXT,
                  fontSize:12
                }}
              />
            )}
            <Bar dataKey="total" barSize={barSize}>
              <LabelList
                dataKey="total"
                position="top"
                formatter={v=>v.toLocaleString("es-MX")}
                style={labelStyle}
              />
              {rows.map((row,i)=>(
                <Cell
                  key={i}
                  fill={colorMap
                    ? colorMap[row[dataKeyX]] || "#666"
                    : defaultColors[i % defaultColors.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </>
    );
  };

  /* ========= INYECTORA CHART + KPIs ========= */
  const inyChart = useMemo(() => {
    if (!hasInyectora) return null;
    const filtered = data.filter(r=>r.inyectora.toLowerCase()===term);

    // 1) producción real
    const totalProd = filtered.reduce((sum,{total_producido})=>sum+Number(total_producido),0);

    // 2) meses únicos en filtered
    const meses = Array.from(new Set(filtered.map(r=>r.fecha.slice(0,7))));
    let diasLV=0, diasSab=0, diasTot=0, turnosTeor=0;
    meses.forEach(mes=>{
      const [y,m] = mes.split("-");
      const info = getMonthWorkInfo(Number(y), Number(m)-1);
      diasLV += info.diasLV;
      diasSab += info.diasSab;
      diasTot += info.diasTotales;
      turnosTeor += info.turnosTeoricos;
    });

    // 3) capacidad teórica en turno
    const capTurno = CAPACIDAD_DIA_100/3;
    const capacidadTeorica = Math.round(turnosTeor * capTurno);

    // 4) turnos trabajados reales
    const turnosTrab = new Set(filtered.map(r=>`${r.fecha}|${r.turno}`)).size;

    // 5) días equivalentes = turnosTrab/3
    const diasTrabEq = turnosTrab/3;

    // 6) porcentajes
    const pctUtil = capacidadTeorica
      ? (totalProd/capacidadTeorica)*100
      : 0;
    const pctTurnos = turnosTeor
      ? (turnosTrab/turnosTeor)*100
      : 0;

    // 7) datos para barras
    const grouped = filtered.reduce((acc,{turno,total_producido})=>{
      acc[turno] = (acc[turno]||0)+Number(total_producido);
      return acc;
    },{});
    const rows = Object.entries(grouped).map(([turno,total])=>({turno,total}));

    return (
      <>
        <h3 className="oi-chart-title text-center mb-2">
          Productividad Inyectora: {busquedaOp}
        </h3>

        <div className="oi-kpis mb-4">
          <div className="oi-kpi">
            <span className="label">Producción real</span>
            <span className="value">{totalProd.toLocaleString("es-MX")} pzs</span>
          </div>
          <div className="oi-kpi">
            <span className="label">Capacidad teórica</span>
            <span className="value">{capacidadTeorica.toLocaleString("es-MX")} pzs</span>
          </div>
          <div className="oi-kpi">
            <span className="label">% Utilización</span>
            <span className="value">{pctUtil.toFixed(1)}%</span>
          </div>
          <div className="oi-kpi">
            <span className="label">Turnos trabajados</span>
            <span className="value">
              {turnosTrab} / {turnosTeor} ({pctTurnos.toFixed(1)}%)
            </span>
          </div>
          <div className="oi-kpi">
            <span className="label">Días trabajados (eq.)</span>
            <span className="value">
              {diasTrabEq.toFixed(1)} / {diasTot} ({diasLV} L–V, {diasSab} Sáb)
            </span>
          </div>
        </div>

        <ChartBlock
          title=""
          dataKeyX="turno"
          rows={rows}
          colorMap={turnoColors}
          capLine={null}
        />
      </>
    );
  }, [hasInyectora, data, term, busquedaOp]);

  /* ===== OPERADOR CHART ===== */
  const opChart = useMemo(() => {
    if (!hasOperador) return null;
    const filtered = data.filter(r=>r.operador.toLowerCase()===term);
    const grouped = filtered.reduce((acc,{inyectora,total_producido})=>{
      acc[inyectora]=(acc[inyectora]||0)+Number(total_producido);
      return acc;
    },{});
    const rows = Object.entries(grouped).map(([inyectora,total])=>({inyectora,total}));
    return (
      <ChartBlock
        title={`Trayecto de ${busquedaOp} por inyectora`}
        dataKeyX="inyectora"
        rows={rows}
      />
    );
  },[hasOperador,data,term,busquedaOp]);

  /* ===== TURNO CHART ===== */
  const turnoChart = useMemo(() => {
    if (!hasTurno) return null;
    const filtered = data.filter(r=>r.turno.toLowerCase()===term);
    const grouped = filtered.reduce((acc,{operador,total_producido})=>{
      acc[operador]=(acc[operador]||0)+Number(total_producido);
      return acc;
    },{});
    const top5 = Object.entries(grouped)
      .map(([operador,total])=>({operador,total}))
      .sort((a,b)=>b.total-a.total)
      .slice(0,5);
    return (
      <ChartBlock
        title={`Top 5 Operadores - ${busquedaOp}`}
        dataKeyX="operador"
        rows={top5}
      />
    );
  },[hasTurno,data,term,busquedaOp]);

  if (noData) {
    return <div className="oi-graficas text-center py-5">
      No hay datos para "{busquedaOp}".
    </div>;
  }

  return (
    <div className="oi-graficas">
      {inyChart}
      {opChart}
      {turnoChart}
    </div>
  );
};

export default Graficas;
